import { ReactElement, useState } from 'react'
import { useParams } from 'react-router-dom'
import { documentationRootGuid, projectFilesRootGuid } from '@constants'
import { Delete } from '@mui/icons-material'
import { CircularProgress, IconButton } from '@mui/material'
import { getGetFileUsagesQueryKey } from '@api/document-attachments/document-attachments'
import { useIsMutating, useQueryClient } from '@tanstack/react-query'
import { useDeleteDocument } from 'src/components/pages/Editor/hooks/requests'
import FileUsageDialog from '../../../FileUsageDialog'

interface Props {
  docGuid: string
  docName?: string
  rootGuid?: string
  onDelete?: () => void
}

const DeleteButton = ({
  docGuid,
  docName,
  rootGuid = documentationRootGuid,
  onDelete,
}: Props): ReactElement => {
  const { projectId } = useParams()
  const queryClient = useQueryClient()
  // A project file still shown by document items: they are handled in a dialog first
  const [isUsageDialogOpen, setIsUsageDialogOpen] = useState(false)

  const { mutate: deleteDoc, isLoading: isDeleting } = useDeleteDocument(rootGuid, {
    onDeleteSuccess: () => {
      setIsUsageDialogOpen(false)
      onDelete?.()
    },
    onFileInUse: () => {
      setIsUsageDialogOpen(true)
      queryClient.invalidateQueries(
        getGetFileUsagesQueryKey(projectId as string, projectFilesRootGuid, docGuid),
      )
    },
  })

  const isAnyDeleting = useIsMutating(['deleteProjectDocument']) > 0

  return (
    <>
      <IconButton
        size="small"
        onClick={(e: { stopPropagation: () => void }) => {
          e.stopPropagation()
          deleteDoc(docGuid)
        }}
        disabled={isAnyDeleting || isDeleting}
        data-cy="btn-delete-document"
      >
        {isDeleting ? (
          <CircularProgress size={18} color="inherit" />
        ) : (
          <Delete fontSize="inherit" />
        )}
      </IconButton>

      {isUsageDialogOpen && (
        <FileUsageDialog
          open
          fileGuid={docGuid}
          fileName={docName}
          isDeletingFile={isDeleting}
          onDeleteFile={() => deleteDoc(docGuid)}
          onClose={() => setIsUsageDialogOpen(false)}
        />
      )}
    </>
  )
}

export default DeleteButton
